import ContradictoireConfirmation from "./ContradictoireConfirmation";
import DetectionResultBox from "./DetectionResultBox";
import EpHistoryList from "./EpHistoryList";
import MainReasonSelector from "./MainReasonSelector";
import ModerationReasonsSelector from "./ModerationReasonsSelector";
import OrganizationSelector from "./OrganizationSelector";
import PregnancyCheckbox from "./PregnancyCheckbox";
import RecipientInfoHeader from "./RecipientInfoHeader";
import SanctionProposalSelector from "./SanctionProposalSelector";
import SanctionTypeSelector from "./SanctionTypeSelector";
import { MAIN_REASONS, SANCTION_TYPES } from "./sanctionConstants";
import useAddRecipientToEp from "./useAddRecipientToEp";

const AddRecipientToEp = ({ recipient, refresh = () => {} }) => {
  const {
    formData,
    setFormData,
    isSaving,
    errors,
    sanctionProposals,
    detectionResult,
    showProposals,
    setShowProposals,
    loadingProposals,
    proposalsError,
    epHistory,
    loadingContext,
    availableOrganizations,
    mainReasonsConfig,
    sddStatus,
    rsaType,
    age,
    maritalStatus,
    save,
    isFormValid,
    closeModal,
  } = useAddRecipientToEp(recipient, refresh);

  return (
    <div>
      <div className="border-bottom  mb-2">
        <h4 style={{ fontSize: 20, color: "#333", marginBottom: 5 }}>
          Inscrire un allocataire à la prochaine Équipe Pluridisciplinaire
        </h4>
        <p className="text-muted mb-0" style={{ fontSize: 14 }}>
          Depuis la page de détail de l'allocataire
        </p>
      </div>

      <div className="row">
        {/* Colonne 1 : Informations allocataire + Historique EP */}
        <div className="col-lg-4 col-md-12 mb-4">
          <div style={{ position: "sticky", top: 0 }}>
            <RecipientInfoHeader
              recipient={recipient}
              age={age}
              sddStatus={sddStatus}
              maritalStatus={maritalStatus}
              rsaType={rsaType}
            />
            <div
              className="p-3 rounded"
              style={{
                background: "#f8f9fa",
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 280px)",
              }}
            >
              <h5 className="mb-3" style={{ fontSize: 16, color: "#333" }}>
                📋 Historique des EP de l'allocataire
              </h5>
              <div style={{ overflowY: "auto", flex: 1 }}>
                <EpHistoryList epHistory={epHistory} />
              </div>
            </div>
          </div>
        </div>

        {/* Colonne 2 : Formulaire d'inscription */}
        <div className="col-lg-8 col-md-12">
          {loadingContext && (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Chargement...</span>
              </div>
              <p className="mt-2 mb-0" style={{ fontSize: 14 }}>
                Vérification de l'éligibilité...
              </p>
            </div>
          )}
          {!loadingContext && (
            <>
              {age && age > 62 && (
                <div
                  className="alert alert-warning border-warning mb-3"
                  style={{ fontWeight: 600 }}
                >
                  ⚠️ ATTENTION : Cet allocataire a plus de 62 ans. Les
                  allocataires de plus de 62 ans ne doivent pas être
                  sanctionnés.
                </div>
              )}

              <OrganizationSelector
                availableOrganizations={availableOrganizations}
                value={formData.organizationOrigin}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    organizationOrigin: e.target.value,
                  })
                }
              />

              <ContradictoireConfirmation
                checked={formData.contradictoire}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contradictoire: e.target.checked,
                  })
                }
              />

              <PregnancyCheckbox
                checked={formData.pregnancy}
                onChange={(e) =>
                  setFormData({ ...formData, pregnancy: e.target.checked })
                }
              />

              {formData.contradictoire && (
                <>
                  <MainReasonSelector
                    mainReasons={MAIN_REASONS}
                    mainReasonsConfig={mainReasonsConfig}
                    selectedReason={formData.mainReason}
                    selectedPathType={formData.pathType}
                    confirmNotBusinessCreation={
                      formData.confirmNotBusinessCreation
                    }
                    onReasonChange={(reasonId) => {
                      const newReasonConfig = mainReasonsConfig.find(
                        (r) => r.id === reasonId,
                      );
                      const availableTypes =
                        newReasonConfig?.typesSanctions || [];
                      const currentTypeStillAvailable = availableTypes.some(
                        (t) => t.id === formData.sanctionType && !t.erreur,
                      );

                      setFormData({
                        ...formData,
                        mainReason: reasonId,
                        pathType: "",
                        sanctionType: currentTypeStillAvailable
                          ? formData.sanctionType
                          : "",
                        confirmNotBusinessCreation: false,
                      });
                    }}
                    onPathTypeChange={(pathTypeId) =>
                      setFormData({
                        ...formData,
                        pathType: pathTypeId,
                        confirmNotBusinessCreation: false,
                      })
                    }
                    onConfirmNotBusinessCreationChange={(checked) =>
                      setFormData({
                        ...formData,
                        confirmNotBusinessCreation: checked,
                      })
                    }
                  />

                  {formData.mainReason && (
                    <>
                      <ModerationReasonsSelector
                        selectedReasons={formData.secondaryReasons}
                        onAdd={(reasonId) =>
                          setFormData({
                            ...formData,
                            secondaryReasons: [
                              ...formData.secondaryReasons,
                              reasonId,
                            ],
                          })
                        }
                        onRemove={(reasonId) =>
                          setFormData({
                            ...formData,
                            secondaryReasons: formData.secondaryReasons.filter(
                              (r) => r !== reasonId,
                            ),
                          })
                        }
                      />

                      <SanctionTypeSelector
                        sanctionTypes={SANCTION_TYPES}
                        availableSanctionTypes={
                          mainReasonsConfig.find(
                            (r) => r.id === formData.mainReason,
                          )?.typesSanctions || []
                        }
                        selectedType={formData.sanctionType}
                        onTypeChange={(sanctionType) => {
                          setFormData({
                            ...formData,
                            sanctionType,
                          });
                          setShowProposals(false);
                        }}
                      />

                      {formData.sanctionType && (
                        <DetectionResultBox detectionResult={detectionResult} />
                      )}

                      <SanctionProposalSelector
                        loadingProposals={loadingProposals}
                        proposalsError={proposalsError}
                        showProposals={showProposals}
                        sanctionProposals={sanctionProposals}
                        selectedProposal={formData.selectedProposal}
                        onProposalSelect={(proposalId) =>
                          setFormData({
                            ...formData,
                            selectedProposal: proposalId,
                          })
                        }
                      />
                    </>
                  )}
                </>
              )}

              {!isFormValid() &&
                formData.contradictoire &&
                formData.mainReason && (
                  <div className="alert alert-danger" style={{ fontSize: 14 }}>
                    ⚠️ Veuillez compléter tous les champs obligatoires pour
                    pouvoir procéder à l'inscription.
                  </div>
                )}

              <div
                className="d-flex justify-content-end border-top pt-3 mt-3"
                style={{ gap: 10 }}
              >
                {Object.values(errors).map((err) => (
                  <span
                    className="badge badge-danger d-center mr-2 rounded"
                    key={`error${err}`}
                  >
                    {err}
                  </span>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={save}
                  disabled={
                    !isFormValid() ||
                    isSaving ||
                    proposalsError ||
                    (formData.sanctionType &&
                      formData.mainReason &&
                      sanctionProposals.length === 0 &&
                      !loadingProposals)
                  }
                >
                  {isSaving ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-2" />
                      Inscription en cours...
                    </>
                  ) : (
                    <>Inscrire à la prochaine EP disponible</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddRecipientToEp;
